import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Menu from './Menu';
import apiCall from '../../utils/api';
import theme from '../../theme';
import nb_formations from '../../assets/nb_formations.png';
import nb_clique from '../../assets/nb_clique.png';
import {
  ContentContainer,
  MainGrid,
  LeftColumn,
  SummaryCard,
  IconCircle,
  SummaryNumber,
  SummarySubtitle,
  GraphContainer,
  GraphHeader,
  DataTypeSelector,
  DataTab,
  TitleTimelineWrapper,
  GraphTitle,
  TimelineSelector,
  TimelineButton,
  ChartWrapper,
  TableTitle,
  TableContainer,
  StyledTable,
  Th,
  Td,
  Tr,
  GraphSubtitle,
  InfoText,
  ShowMoreButton,
  RowNumberTh,
  RowNumberTd,
} from './AnalyticsStyles';

const formatDateLabel = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
};

const Analytics = () => {
  const [analyticsData, setAnalyticsData] = useState({});
  const [managerData, setManagerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDataType, setSelectedDataType] = useState('impressions');
  const [selectedTimeline, setSelectedTimeline] = useState('30d');
  const [visibleFormationsCount, setVisibleFormationsCount] = useState(20);
  const [isSpecialUser, setIsSpecialUser] = useState(false);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch manager data first
        const managerInfo = await apiCall(
          `${process.env.REACT_APP_BACKEND_URL}/manager/me/`,
          'GET'
        );
        setManagerData(managerInfo);
        if (managerInfo.email === 'dimitri@major-prepa.com') {
          setIsSpecialUser(true);
        }

        const response = await apiCall(
          `${process.env.REACT_APP_BACKEND_URL}/manager/analytics/`,
          'GET'
        );

        if (typeof response !== 'object' || response === null) {
          throw new Error("Invalid data format received from backend.");
        }

        setAnalyticsData(response);

        if (Object.keys(response).length === 0) {
          console.log("No analytics data found for this manager.");
        }

      } catch (err) {
        console.error('Error fetching analytics:', err);
        setError(`Failed to fetch analytics data: ${err.message}. Please try again.`);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  const totalFormations = useMemo(() => {
    return Object.keys(analyticsData).length;
  }, [analyticsData]);

  const overallTotalInteractions = useMemo(() => {
    return Object.values(analyticsData).reduce((sum, formation) => {
      return sum +
             (formation.total_site_clicks || 0) +
             (formation.total_parcoursup_clicks || 0) +
             (formation.total_favorite_adds || 0) +
             (formation.total_letters_generated || 0) +
             (formation.total_comparator_adds || 0) +
             (formation.total_comparator_removes || 0) +
             (formation.total_letters_shared || 0) +
             (formation.total_formation_shared || 0) +
             (formation.total_major_prepa_clicks || 0);
    }, 0);
  }, [analyticsData]);

  const processedChartData = useMemo(() => {
      const now = new Date();
      let startDate = new Date();
      let timeUnit = 'day'; // Default to day

      // Determine start date based on timeline
      switch (selectedTimeline) {
          case '24h':
              startDate.setDate(now.getDate() - 1);
              timeUnit = 'hour';
              break;
          case '7d':
              startDate.setDate(now.getDate() - 7);
              break;
          case '30d':
              startDate.setMonth(now.getMonth() - 1);
              break;
          case '90d':
              startDate.setMonth(now.getMonth() - 3);
              break;
          case '1a':
              startDate.setFullYear(now.getFullYear() - 1);
              break;
          default: // Should not happen, but default to 30 days
              startDate.setMonth(now.getMonth() - 1);
      }
       // Ensure start date is not in the future if timeline causes wrap-around near year/month end
       if (startDate > now) {
           startDate = new Date(now);
           startDate.setHours(0, 0, 0, 0); // Reset to beginning of today if day based
           if (timeUnit === 'hour') {
               startDate.setHours(now.getHours() - 23); // Ensure we cover 24 hours back if needed
           }
       }

      // Aggregate existing data by date (or hour for 24h)
      const dailyAggregated = {};
      Object.values(analyticsData).forEach(formation => {
      if (formation.interactions_by_date && typeof formation.interactions_by_date === 'object') {
              Object.entries(formation.interactions_by_date).forEach(([dateStr, counts]) => {
                  const itemDate = new Date(dateStr);
                  // Filter out data outside the selected timeline early
                  if (itemDate.getTime() < startDate.getTime()) return;

                  let key = dateStr;
                  if (timeUnit === 'hour') {
                      // For 24h view, aggregate by hour
                      key = `${dateStr}T${String(itemDate.getHours()).padStart(2, '0')}:00`;
                  }

                  if (!dailyAggregated[key]) {
                      dailyAggregated[key] = {
                          date: itemDate.toISOString(), // Store full ISO string
                          impressions: 0,
                          views: 0, siteClicks: 0, parcoursupClicks: 0, favoriteAdds: 0,
                          lettersGenerated: 0, comparatorInteractions: 0, sharingInteractions: 0,
                          majorPrepaClicks: 0,
                          total: 0
                      };
                  }
                  
                  // Sum up counts (same logic as before)
                  const impressionCount = counts.ranking_impression || 0;
                  const viewCount = counts.view || 0;
                  const siteCount = counts.site || 0;
                  const parcoursupCount = counts.parcoursup || 0;
                  const favAddCount = counts.favorite_add || 0;
                  const letterGenCount = counts.letter_generated || 0;
                  const compAddCount = counts.comparator_add || 0;
                  const compRemCount = counts.comparator_remove || 0;
                  const letterShareCount = counts.letter_shared || 0;
                  const formShareCount = counts.formation_shared || 0;
                  const majorPrepaClickCount = counts.major_prepa_click || 0;

                  dailyAggregated[key].impressions += impressionCount;
                  dailyAggregated[key].views += viewCount;
                  dailyAggregated[key].siteClicks += siteCount;
                  dailyAggregated[key].parcoursupClicks += parcoursupCount;
                  dailyAggregated[key].favoriteAdds += favAddCount;
                  dailyAggregated[key].lettersGenerated += letterGenCount;
                  dailyAggregated[key].comparatorInteractions += (compAddCount + compRemCount);
                  dailyAggregated[key].sharingInteractions += (letterShareCount + formShareCount);
                  dailyAggregated[key].majorPrepaClicks += majorPrepaClickCount;
              });
          }
      });

      // After aggregating all data, calculate the total for each day
      Object.values(dailyAggregated).forEach(dayData => {
          dayData.total = dayData.siteClicks +
                          dayData.parcoursupClicks +
                          dayData.favoriteAdds +
                          dayData.lettersGenerated +
                          dayData.comparatorInteractions +
                          dayData.sharingInteractions;
      });

      // Generate baseline data for the full timeline
      const baselineData = [];
      let currentDate = new Date(startDate);
      const endDate = new Date(now);

      while (currentDate <= endDate) {
          let key = currentDate.toISOString().split('T')[0]; // YYYY-MM-DD for daily
          let displayDate = formatDateLabel(currentDate.toISOString());

          if (timeUnit === 'hour') {
               // For 24h view, use hour key and potentially different label format
               key = `${currentDate.toISOString().split('T')[0]}T${String(currentDate.getHours()).padStart(2, '0')}:00`;
               // Display only the hour for 24h view
               displayDate = `${String(currentDate.getHours()).padStart(2, '0')}h`;
          }

          // Check if data exists for this key, otherwise use default zero values
          const existingData = dailyAggregated[key];
          baselineData.push({
              date: currentDate.toISOString(), // Keep full ISO string for potential sorting/tooltip
              displayDate: displayDate,
              impressions: existingData?.impressions || 0,
              views: existingData?.views || 0,
              siteClicks: existingData?.siteClicks || 0,
              parcoursupClicks: existingData?.parcoursupClicks || 0,
              favoriteAdds: existingData?.favoriteAdds || 0,
              lettersGenerated: existingData?.lettersGenerated || 0,
              comparatorInteractions: existingData?.comparatorInteractions || 0,
              sharingInteractions: existingData?.sharingInteractions || 0,
              majorPrepaClicks: existingData?.majorPrepaClicks || 0,
              total: existingData?.total || 0,
          });

          // Increment date
          if (timeUnit === 'hour') {
              currentDate.setHours(currentDate.getHours() + 1);
          } else {
              currentDate.setDate(currentDate.getDate() + 1);
          }
      }

      // Ensure the final array is sorted by date (important for line chart)
      return baselineData.sort((a, b) => new Date(a.date) - new Date(b.date));

  }, [analyticsData, selectedTimeline]);

  const chartTitleInfo = useMemo(() => {
      let count = 0;
      let label = 'interactions';

      processedChartData.forEach(item => {
         switch (selectedDataType) {
             case 'impressions': count += item.impressions; label = 'impressions'; break;
             case 'views': count += item.views; label = 'visites'; break;
             case 'parcoursup': count += item.parcoursupClicks; label = 'clics Parcoursup'; break;
             case 'site': count += item.siteClicks; label = 'clics site école'; break;
             case 'favorite_add': count += item.favoriteAdds; label = 'ajouts aux favoris'; break;
             case 'letters': count += item.lettersGenerated; label = 'lettres générées'; break;
             case 'comparator': count += item.comparatorInteractions; label = 'actions comparateur'; break;
             case 'sharing': count += item.sharingInteractions; label = 'partages'; break;
             case 'majorPrepa': count += item.majorPrepaClicks; label = 'clics Major Prépa'; break;
             default: 
                 count += item.total;
                 label = 'interactions';
         }
      });

      return { count, label };
  }, [processedChartData, selectedDataType]);

  const sortedFormationsTableData = useMemo(() => {
    return Object.values(analyticsData)
      .map(formation => {
        const interactions = (formation.total_site_clicks || 0) +
                             (formation.total_parcoursup_clicks || 0) +
                             (formation.total_favorite_adds || 0) +
                             (formation.total_letters_generated || 0) +
                             (formation.total_comparator_adds || 0) +
                             (formation.total_comparator_removes || 0) +
                             (formation.total_letters_shared || 0) +
                             (formation.total_formation_shared || 0);
        return {
          id: formation.id,
          parcoursupCode: formation.parcoursupCode,
          name: `${formation.name} - ${formation.etablissement_nom || ''}`,
          views: formation.total_views || 0,
          interactions: interactions,
          impressions: formation.total_impressions || 0,
          lettersGenerated: formation.total_letters_generated || 0,
          comparatorInteractions: (formation.total_comparator_adds || 0) + (formation.total_comparator_removes || 0),
          sharingInteractions: (formation.total_letters_shared || 0) + (formation.total_formation_shared || 0),
        };
      })
      .sort((a, b) => {
        if (b.interactions !== a.interactions) {
          return b.interactions - a.interactions;
        }
        return b.views - a.views; // Secondary sort by views if interactions are equal
      });
  }, [analyticsData]);

  const handleShowMore = () => {
    setVisibleFormationsCount(prevCount => prevCount + 20); // Load 20 more
  };

  useEffect(() => {
    console.log("-------------------- ANALYTICS DEBUG --------------------");
    console.log("[Source 1] Lifetime Total Interactions ('nombre d'interactions total'):", overallTotalInteractions);
    console.log(`[Source 2] Graph Total ('Vous avez généré X interactions...') for timeline '${selectedTimeline}' and type '${selectedDataType}':`, chartTitleInfo.count);
    console.log("---------------------------------------------------------");
    console.log("Raw analytics data from backend:", analyticsData);
    console.log("Processed data for chart:", processedChartData);

  }, [overallTotalInteractions, chartTitleInfo, selectedTimeline, selectedDataType, analyticsData, processedChartData]);

  if (loading) {
    return (
      <Menu>
        <ContentContainer>
          <p>Chargement des données analytiques...</p>
        </ContentContainer>
      </Menu>
    );
  }

  return (
    <Menu>
      <ContentContainer>
        {error && !loading && <p style={{ color: 'red' }}>Erreur: {error}</p>}

        <MainGrid>
          <LeftColumn>
            <SummaryCard>
              <IconCircle>
                <img src={nb_formations} alt="Nombre de formations" />
              </IconCircle>
              <SummaryNumber>{totalFormations}</SummaryNumber>
              <SummarySubtitle>nombre de formations</SummarySubtitle>
            </SummaryCard>
            <SummaryCard>
              <IconCircle>
                <img src={nb_clique} alt="Nombre d'interactions" />
              </IconCircle>
              <SummaryNumber>{overallTotalInteractions}</SummaryNumber>
              <SummarySubtitle>nombre d'interactions total</SummarySubtitle>
            </SummaryCard>
          </LeftColumn>

          <GraphContainer>
             <GraphHeader>
                <DataTypeSelector>
                    <DataTab isSelected={selectedDataType === 'impressions'} onClick={() => setSelectedDataType('impressions')}>Impressions</DataTab>
                    <DataTab isSelected={selectedDataType === 'views'} onClick={() => setSelectedDataType('views')}>Visites</DataTab>
                    <DataTab isSelected={selectedDataType === 'total'} onClick={() => setSelectedDataType('total')}>Interactions</DataTab>
                    {isSpecialUser && (
                        <DataTab isSelected={selectedDataType === 'majorPrepa'} onClick={() => setSelectedDataType('majorPrepa')}>Major Prépa</DataTab>
                    )}
                </DataTypeSelector>
                 <TitleTimelineWrapper>
                     <div style={{ textAlign: 'left' }}>
                         <GraphTitle>
                             Vous avez généré <span>{chartTitleInfo.count}</span> {chartTitleInfo.label} sur cette période
                         </GraphTitle>
                         <GraphSubtitle>
                            {selectedDataType === 'impressions' && "Les impressions regroupent le nombre de formations qui sont apparus dans le top 50 des utilisateurs."}
                            {selectedDataType === 'views' && "Chaque fois qu'un utilisateur parcourt une fiche Formation, cela correspond à une visite."}
                            {selectedDataType === 'total' && "Les interactions regroupent les clics sur le site de l'école ou la fiche Parcoursup, les formations mises en favori, comparées, partagées ou pour laquelle l'étudiant a écrit une lettre."}
                            {isSpecialUser && selectedDataType === 'majorPrepa' && "Le nombre de clics sur le bouton Major Prépa sur les fiches des CPGE EC et Lettres et sur les liens renvoyés par Meo dans le chat."}
                         </GraphSubtitle>
                     </div>
                     <TimelineSelector>
                         <TimelineButton isSelected={selectedTimeline === '24h'} onClick={() => setSelectedTimeline('24h')}>24h</TimelineButton>
                         <TimelineButton isSelected={selectedTimeline === '7d'} onClick={() => setSelectedTimeline('7d')}>1s</TimelineButton>
                         <TimelineButton isSelected={selectedTimeline === '30d'} onClick={() => setSelectedTimeline('30d')}>1m</TimelineButton>
                         <TimelineButton isSelected={selectedTimeline === '90d'} onClick={() => setSelectedTimeline('90d')}>3m</TimelineButton>
                         <TimelineButton isSelected={selectedTimeline === '1a'} onClick={() => setSelectedTimeline('1a')}>1a</TimelineButton>
                     </TimelineSelector>
                 </TitleTimelineWrapper>
             </GraphHeader>
              <ChartWrapper>
                    <ResponsiveContainer width="100%" height="100%">
                         <LineChart data={processedChartData} margin={{ top: 5, right: 20, bottom: 25, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
                             <XAxis
                                dataKey="displayDate"
                                stroke="#29435B"
                                style={{ fontFamily: 'Fustat, sans-serif', fontSize: '13px', fontWeight: 500 }}
                                interval="preserveStartEnd"
                                tick={{ fill: '#29435B' }}
                             />
                             <YAxis stroke="#666" />
                             <Tooltip contentStyle={{ fontFamily: 'Fustat, sans-serif' }}/>
                             <Line
                                type="monotone"
                                dataKey={
                                    selectedDataType === 'impressions' ? 'impressions' :
                                    selectedDataType === 'views' ? 'views' :
                                    selectedDataType === 'parcoursup' ? 'parcoursupClicks' :
                                    selectedDataType === 'site' ? 'siteClicks' :
                                    selectedDataType === 'favorite_add' ? 'favoriteAdds' :
                                    selectedDataType === 'letters' ? 'lettersGenerated' :
                                    selectedDataType === 'comparator' ? 'comparatorInteractions' :
                                    selectedDataType === 'sharing' ? 'sharingInteractions' :
                                    selectedDataType === 'majorPrepa' ? 'majorPrepaClicks' :
                                    'total'
                                }
                                stroke={theme.colors.bleu_fonce}
                                strokeWidth={2}
                                dot={false}
                                name={
                                    selectedDataType === 'impressions' ? 'Impressions' :
                                    selectedDataType === 'views' ? 'Visites' :
                                    selectedDataType === 'parcoursup' ? 'Clics Parcoursup' :
                                    selectedDataType === 'site' ? 'Clics Site' :
                                    selectedDataType === 'favorite_add' ? 'Ajouts Favoris' :
                                    selectedDataType === 'letters' ? 'Lettres Générées' :
                                    selectedDataType === 'comparator' ? 'Actions Comparateur' :
                                    selectedDataType === 'sharing' ? 'Partages' :
                                    selectedDataType === 'majorPrepa' ? 'Clics Major Prépa' :
                                    'Interactions'
                                }
                             />
                      </LineChart>
                    </ResponsiveContainer>
              </ChartWrapper>
             <InfoText>
                *
                {selectedDataType === 'impressions'
                    ? "Les données d'impressions sont disponibles depuis le 24 juin 2025"
                    : "Les données sont disponibles depuis le 16 avril 2025"
                }
             </InfoText>
          </GraphContainer>
        </MainGrid>

        <TableTitle>Mes formations</TableTitle>
        <TableContainer>
            {sortedFormationsTableData.length > 0 ? (
                <StyledTable>
                    <thead>
                        <Tr>
                            <RowNumberTh>#</RowNumberTh>
                            <Th>Nom</Th>
                            <Th align="center">Lien Meoria</Th>
                            <Th align="center">Impressions</Th>
                            <Th align="center">Visites</Th>
                            <Th align="center">Interactions</Th>
                        </Tr>
                    </thead>
                    <tbody>
                        {sortedFormationsTableData.slice(0, visibleFormationsCount).map((formation, index) => (
                            <Tr key={formation.id}>
                                <RowNumberTd>{index + 1}</RowNumberTd>
                                <Td>{formation.name}</Td>
                                <Td align="center"><a href={`/formation?id=${formation.id}`} target="_blank" rel="noopener noreferrer">Voir fiche</a></Td>
                                <Td align="center">{formation.impressions}</Td>
                                <Td align="center">{formation.views}</Td>
                                <Td align="center">{formation.interactions}</Td>
                            </Tr>
                        ))}
                    </tbody>
                </StyledTable>
             ) : (
                 <p style={{ textAlign: 'center', color: '#666' }}>Aucune formation à afficher.</p>
            )}
            {sortedFormationsTableData.length > visibleFormationsCount && (
              <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                <ShowMoreButton onClick={handleShowMore}>
                  Voir plus
                </ShowMoreButton>
              </div>
            )}
        </TableContainer>

      </ContentContainer>
    </Menu>
  );
};

export default Analytics; 